import React from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Select,
  Stack,
  Switch,
  Text,
} from "@chakra-ui/react";

import { IsProduction } from "../helpers/config";
import {
  clearAllFlagOverrides,
  clearFlagOverride,
  getBoolFlag,
  getFlagOverride,
  getFlagType,
  getAllowedValues,
  getNumberFlag,
  getRegisteredFlagKeys,
  getStringFlag,
  setFlagOverride,
  subscribeToFlagChanges,
} from "../services/featureFlags";

// Floating dev-only panel for flipping feature flags locally without touching
// the Firebase console. Web equivalent of mobile's FeatureFlagsScreen. Renders
// nothing in production builds.

function FlagRow({ flagKey }) {
  const type = getFlagType(flagKey);
  const allowed = getAllowedValues(flagKey);
  const overridden = getFlagOverride(flagKey) !== undefined;

  return (
    <Box py={2} borderBottomWidth="1px" borderColor="gray.200">
      <Flex justify="space-between" align="center" mb={1}>
        <Text fontSize="sm" fontWeight="semibold" fontFamily="mono">
          {flagKey}
        </Text>
        {overridden && (
          <Badge colorScheme="orange" fontSize="0.6rem">
            override
          </Badge>
        )}
      </Flex>

      {type === "boolean" && (
        <FormControl display="flex" alignItems="center">
          <FormLabel htmlFor={flagKey} mb="0" fontSize="sm">
            {getBoolFlag(flagKey) ? "on" : "off"}
          </FormLabel>
          <Switch
            id={flagKey}
            isChecked={getBoolFlag(flagKey)}
            onChange={(e) => setFlagOverride(flagKey, e.target.checked)}
          />
        </FormControl>
      )}

      {type === "string" && allowed && (
        <Select
          size="sm"
          value={getStringFlag(flagKey)}
          onChange={(e) => setFlagOverride(flagKey, e.target.value)}
        >
          {allowed.map((v) => (
            <option key={String(v)} value={String(v)}>
              {String(v)}
            </option>
          ))}
        </Select>
      )}

      {type === "string" && !allowed && (
        <Input
          size="sm"
          value={getStringFlag(flagKey)}
          onChange={(e) => setFlagOverride(flagKey, e.target.value)}
        />
      )}

      {type === "number" && (
        <Input
          size="sm"
          type="number"
          value={getNumberFlag(flagKey)}
          onChange={(e) => setFlagOverride(flagKey, Number(e.target.value))}
        />
      )}

      {overridden && (
        <Button
          mt={1}
          size="xs"
          variant="link"
          colorScheme="blue"
          onClick={() => clearFlagOverride(flagKey)}
        >
          reset to Remote Config
        </Button>
      )}
    </Box>
  );
}

export default function FeatureFlagsPanel() {
  const [open, setOpen] = React.useState(false);
  // Re-render whenever any flag/override changes so rows reflect current state.
  const [, forceRender] = React.useReducer((n) => n + 1, 0);

  React.useEffect(() => subscribeToFlagChanges(forceRender), []);

  if (IsProduction()) return null;

  const keys = getRegisteredFlagKeys();

  return (
    <Box position="fixed" bottom="16px" right="16px" zIndex={2000}>
      {open ? (
        <Box
          bg="white"
          color="gray.800"
          borderWidth="1px"
          borderColor="gray.300"
          borderRadius="md"
          boxShadow="lg"
          p={3}
          w="320px"
          maxH="70vh"
          overflowY="auto"
        >
          <Flex justify="space-between" align="center" mb={2}>
            <Text fontWeight="bold">🚩 Feature flags</Text>
            <Button size="xs" onClick={() => setOpen(false)}>
              close
            </Button>
          </Flex>
          <Stack spacing={0}>
            {keys.map((key) => (
              <FlagRow key={key} flagKey={key} />
            ))}
          </Stack>
          <HStack mt={3} justify="flex-end">
            <Button
              size="xs"
              variant="outline"
              onClick={() => clearAllFlagOverrides()}
            >
              reset all overrides
            </Button>
          </HStack>
        </Box>
      ) : (
        <Button size="sm" colorScheme="blackAlpha" onClick={() => setOpen(true)}>
          🚩 Flags
        </Button>
      )}
    </Box>
  );
}
